/**
 * TeacherVerify — landing for the school-email verification link.
 * Reads ?token from URL, POSTs to /teacher/verify, promotes user to teacher
 * role on success.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2, GraduationCap } from 'lucide-react';
import api from '../lib/api';

export default function TeacherVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState('verifying');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const token = params.get('token');
    if (!token) {
      setState('error');
      setMsg('Verification link missing its token. Apply again from your profile.');
      return;
    }
    (async () => {
      try {
        const r = await api.post('/teacher/verify', { token });
        if (r.data?.status === 'verified') {
          setState('success');
          setMsg(r.data.role || 'teacher');
        } else {
          setState('error');
          setMsg('Verification failed.');
        }
      } catch (e) {
        setState('error');
        setMsg(e.response?.data?.detail || 'Verification failed.');
      }
    })();
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#0A0A0A' }} data-testid="teacher-verify-page">
      <div className="max-w-md w-full border border-[#D4A84A]/20 rounded-md p-7 md:p-8" style={{ background: '#0F0F11' }}>
        <div className="flex items-center gap-2 mb-4">
          <GraduationCap size={16} className="text-[#D4A84A]" />
          <span className="font-hud text-[10px] md:text-xs text-[#D4A84A] tracking-widest uppercase">TSOGN — Teacher Verification</span>
        </div>
        {state === 'verifying' && (
          <div className="flex items-center gap-3 font-mono text-sm text-white/85" data-testid="teacher-verify-progress">
            <Loader2 size={16} className="animate-spin text-[#2F8A92]" />
            Confirming your school email…
          </div>
        )}
        {state === 'success' && (
          <div data-testid="teacher-verify-success">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 size={18} className="text-[#2F8A92]" />
              <span className="font-historical text-2xl text-[#D4A84A] font-light">Verified</span>
            </div>
            <p className="font-mono text-sm text-white/85 leading-relaxed">
              You have teacher review privileges. You can now cast Approve / Fail votes on chains in teacher review and submit lessons through the contest.
            </p>
            <p className="font-mono text-xs text-white/55 mt-3">
              Your contributions will be displayed publicly with your credibility score. Methodology is the contract.
            </p>
            <button
              onClick={() => navigate('/')}
              data-testid="teacher-verify-return-btn"
              className="mt-5 font-hud text-xs tracking-widest text-[#0A0A0A] bg-[#D4A84A] px-4 py-2 hover:bg-[#D4A84A]/85"
            >
              ENTER TSOGN
            </button>
          </div>
        )}
        {state === 'error' && (
          <div data-testid="teacher-verify-error">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle size={18} className="text-[#FF6B6B]" />
              <span className="font-historical text-xl text-[#FF6B6B] font-light">Link not valid</span>
            </div>
            <p className="font-mono text-sm text-white/85 leading-relaxed">{msg}</p>
            <p className="font-mono text-xs text-white/55 mt-3">
              Verification links expire after 24 hours. Sign in and re-apply from your profile.
            </p>
            <button
              onClick={() => navigate('/')}
              className="mt-5 font-hud text-xs tracking-widest text-white/85 border border-[#2F8A92]/30 px-4 py-2 hover:text-[#2F8A92]"
            >
              SIGN IN
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
